import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Target, Radio, Users, TrendingUp } from 'lucide-react';

const Marketing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  const marketingModels = [
    {
      title: "Approach Based",
      description: "Explore comprehensive marketing strategies based on different approaches and methodologies to drive sustainable business growth and market penetration.",
      icon: Target,
      link: "/marketing_approach",
      color: "orange-500"
    },
    {
      title: "Channel Based",
      description: "Discover innovative marketing models focused on various channels and platforms for maximum reach, engagement, and conversion optimization.",
      icon: Radio,
      link: "/marketing_channel",
      color: "orange-500"
    },
    {
      title: "Targeting Based",
      description: "Learn about advanced marketing strategies centered on precise audience targeting, segmentation, and personalized customer experiences.",
      icon: Users,
      link: "/marketing_target",
      color: "orange-600"
    },
    {
      title: "Strategy & Purpose Based",
      description: "Understand data-driven marketing models powered by strategic objectives, business purposes, and measurable outcomes.",
      icon: TrendingUp,
      link: "/marketing_Strategy",
      color: "orange-600"
    }
  ];

  return (
    <div className="bg-black text-white">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative px-8 lg:px-24 py-20 pt-32">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 z-10">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              Marketing Models for<br />EdTech Success
            </h1>
            
            <p className="text-gray-400 text-lg lg:text-xl max-w-4xl mx-auto leading-relaxed">
              Discover powerful marketing frameworks designed to accelerate growth, enhance customer engagement, 
              and drive measurable results in the competitive EdTech landscape.
            </p>

            <div className="flex gap-4 justify-center pt-4">
              <button className="px-8 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition">
                Explore All Models
              </button>
              <button className="px-8 py-3 bg-transparent text-white rounded-xl font-medium hover:bg-white hover:text-black transition border-2 border-white">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Models Grid Section */}
      <section className="relative px-8 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto">
          
          {/* Section Heading */}
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Choose Your Marketing Framework
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl">
              Select the marketing model that aligns with your business objectives and target audience to maximize your impact.
            </p>
          </div>

          {/* Models Grid - 2x2 Layout */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {marketingModels.map((model, index) => {
              const IconComponent = model.icon;
              return (
                <div
                  key={index}
                  onClick={() => navigate(model.link)}
                  className="bg-black border-2 border-gray-500 rounded-2xl p-8 hover:border-orange-500 transition-all duration-300 cursor-pointer group"
                >
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: 'rgba(249, 115, 22, 0.2)' }}>
                    <IconComponent className="w-8 h-8 text-orange-500" strokeWidth={2.5} />
                  </div>

                  {/* Title */}
                  <h4 className="text-2xl font-bold text-white mb-4">
                    {model.title}
                  </h4>

                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {model.description}
                  </p>

                  {/* Learn More Link */}
                  <div className="flex items-center text-orange-500 font-semibold group-hover:text-orange-400 transition-colors">
                    <span>Explore This Model</span>
                    <svg
                      className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Horizontal Line */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent mb-12"></div>

          {/* Bottom CTA Section */}
          <div className="space-y-8">
            <h3 className="text-3xl lg:text-4xl font-bold text-orange-500">
              Why Marketing Models Matter for EdTech?
            </h3>
            
            <p className="text-gray-300 text-lg leading-relaxed max-w-5xl">
              In today's competitive educational technology landscape, understanding and implementing the right marketing model 
              is crucial for sustainable growth. Each framework offers unique insights into customer behavior, market dynamics, 
              and strategic positioning. <b>By leveraging these proven models, you can optimize your marketing efforts, 
              improve ROI, and build lasting relationships with your target audience.</b>
            </p>

            {/* Feature Highlights */}
            <div className="grid md:grid-cols-3 gap-6 pt-8">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-orange-500" />
                </div>
                <h5 className="text-xl font-bold text-white">Strategic Focus</h5>
                <p className="text-gray-400">Align marketing efforts with business objectives</p>
              </div>

              <div className="space-y-3">
                <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-500" />
                </div>
                <h5 className="text-xl font-bold text-white">Audience Insights</h5>
                <p className="text-gray-400">Understand and reach your ideal customers</p>
              </div>

              <div className="space-y-3">
                <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-500" />
                </div>
                <h5 className="text-xl font-bold text-white">Measurable Growth</h5>
                <p className="text-gray-400">Track performance and optimize results</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-8">
              <button className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-3 rounded-lg transition-colors duration-300">
                Start Your Journey
              </button>
              <button className="bg-transparent border-2 border-orange-500 hover:bg-orange-500/10 text-orange-500 font-bold px-8 py-3 rounded-lg transition-colors duration-300">
                Schedule Consultation
              </button>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Marketing;